import styled, {css} from 'styled-components';
import {Link} from 'react-router-dom';
import {getColorFromTheme} from '@ui/ui-utils';
import {FeaturePageContent} from '@ui/molecules/feature-page-header';
import {GoalHealth} from './manager-dashboard.types';

export const Page = styled.div`
  min-height: 100%;
  padding-bottom: 80px;
`;

export const ManagerDashboardContent = styled(FeaturePageContent)<{
  $personView: boolean;
}>`
  padding-top: ${({$personView}) => ($personView ? '24px' : '32px')};

  @media (max-width: 768px) {
    padding-top: 24px;
  }
`;

export const Intro = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 18px;
  }
`;

export const IntroCopy = styled.div`
  max-width: 650px;
`;

export const PersonNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const PersonBreadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;

  a {
    color: ${getColorFromTheme('purple300')};
    font-weight: 600;
    text-decoration: none;
  }

  span[aria-hidden='true'] {
    color: ${getColorFromTheme('textMuted')};
    font-size: 16px;
  }
`;

export const PersonIdentity = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h1,
  p {
    margin: 0;
  }

  h1 {
    color: ${getColorFromTheme('textDark')};
    font: 600 24px/32px Graphik, sans-serif;
  }

  p {
    color: ${getColorFromTheme('textBody')};
    font: 400 14px/22px Graphik, sans-serif;
  }

  strong {
    font-weight: 500;
  }
`;

export const PersonDetailLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PersonSection = styled.section`
  &:not(:last-child) {
    padding-bottom: 24px;
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  }

  > div:first-child {
    margin-bottom: 20px;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

export const Controls = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;

    > button,
    > div {
      width: 100%;
    }
  }
`;

export const FilterControl = styled.div`
  position: relative;
`;

export const CompactFilterButton = styled.button<{
  $open: boolean;
  $inactive?: boolean;
}>`
  display: inline-flex;
  height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid
    ${({$open, theme}) =>
      $open ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textDark')};
  font: 600 14px/22px Graphik, sans-serif;
  white-space: nowrap;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  > svg {
    flex: 0 0 auto;
    color: ${getColorFromTheme('textBody')};
  }

  > svg:last-child {
    color: ${({$inactive, theme}) =>
      $inactive ? theme.colors.textMuted : theme.colors.textBody};
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
    transition: transform 160ms ease;
  }

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }

  &:disabled {
    cursor: default;
  }

  &:disabled:hover {
    border-color: ${getColorFromTheme('borderDark')};
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const FilterDivider = styled.span`
  width: 1px;
  height: 30px;
  background: ${getColorFromTheme('borderLight')};

  @media (max-width: 520px) {
    display: none;
  }
`;

export const FilterMenu = styled.div<{$width?: number}>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 40;
  width: ${({$width}) => `${$width || 298}px`};
  max-width: calc(100vw - 40px);
  padding: 12px 8px;
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 20px rgba(208, 208, 221, 0.43);

  @media (max-width: 520px) {
    right: auto;
    left: 0;
    width: 100%;
  }
`;

export const ScopeMenu = styled(FilterMenu)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid ${getColorFromTheme('borderDark')};

  html[data-theme='dark'] & {
    box-shadow: none;
  }
`;

export const ScopeMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ScopeMenuHeading = styled.div`
  padding: 4px 8px;
  color: ${getColorFromTheme('textDark')};
  font: 500 14px/22px Graphik, sans-serif;
`;

export const ScopeMenuSeparator = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0;
  border: 0;
  background: ${getColorFromTheme('borderLight')};
`;

export const ScopeOption = styled.button<{$active: boolean}>`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const DateMenu = styled(FilterMenu)`
  width: 350px;
  padding: 0;
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderDark')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const DateMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('textDark')};
  font: 600 14px/22px Graphik, sans-serif;
`;

export const DateMenuCancel = styled.button`
  padding: 2px;
  color: ${getColorFromTheme('textBody')};
  font: 500 14px/22px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${getColorFromTheme('purple300')};
  }
`;

export const DateMenuBody = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 12px;
  padding: 16px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const DateField = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/18px Graphik, sans-serif;
`;

export const DateRangeField = styled.div`
  grid-column: 1 / -1;
  min-width: 0;

  > div {
    margin: 0;
  }
`;

export const DateFieldButton = styled.button<{$open: boolean}>`
  display: flex;
  width: 100%;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid
    ${({$open, theme}) =>
      $open ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  svg {
    flex: 0 0 auto;
    color: ${getColorFromTheme('textBody')};
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }
`;

export const DateOptionMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 45;
  width: 100%;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const DateOption = styled.button<{$active: boolean}>`
  display: flex;
  width: 100%;
  min-height: 36px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;

export const DateMenuFooter = styled.div`
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 0 0 9px 9px;
  background: ${getColorFromTheme('white')};
`;

export const DateMenuAction = styled.button<{$primary?: boolean}>`
  min-height: 38px;
  padding: 8px 16px;
  border: 1px solid
    ${({$primary}) =>
      $primary
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${({$primary}) =>
    $primary ? getColorFromTheme('purple300') : getColorFromTheme('white')};
  color: ${({$primary}) =>
    $primary
      ? getColorFromTheme('alwaysWhite')
      : getColorFromTheme('textBody')};
  font: 600 12px/20px Graphik, sans-serif;

  &:disabled {
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const Panel = styled.section`
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  margin-bottom: 24px;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  @media (max-width: 600px) {
    align-items: flex-start;
    padding: 18px 20px;
  }
`;

export const PanelTitle = styled.h2`
  margin: 0;
  color: ${getColorFromTheme('textDark')};
  font-size: ${({theme}) => theme.fontSize.headline4};
  font-weight: ${({theme}) => theme.fontWeight.bold};
  line-height: ${({theme}) => theme.lineHeight.headline4};
`;

export const PanelSubtitle = styled.p`
  margin: 2px 0 0;
  color: ${getColorFromTheme('textBody')};
  font-size: ${({theme}) => theme.fontSize.microcopy};
  line-height: ${({theme}) => theme.lineHeight.microcopy};
`;

export const Row = styled(Link)<{$tone?: string}>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 16px;
  align-items: center;
  min-height: 74px;
  padding: 14px 22px 14px 26px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: inherit;
  text-decoration: none;

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: ${({$tone, theme}) =>
      $tone === 'goal-behind'
        ? theme.colors.red400
        : $tone === 'program-overdue' || $tone === 'goal-risk'
        ? theme.colors.orange300
        : theme.colors.purple300};
    content: '';
  }

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: -2px;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    padding-right: 18px;

    > span:nth-last-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
      justify-self: start;
    }
  }
`;

export const RowTitle = styled.div`
  min-width: 0;

  strong {
    display: block;
    overflow: hidden;
    color: ${getColorFromTheme('textDark')};
    font-size: ${({theme}) => theme.fontSize.body2};
    font-weight: ${({theme}) => theme.fontWeight.bold};
    line-height: ${({theme}) => theme.lineHeight.body2};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    display: block;
    overflow: hidden;
    color: ${getColorFromTheme('textBody')};
    font-size: ${({theme}) => theme.fontSize.microcopy};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StatusPill = styled.span<{
  $status: GoalHealth | 'overdue' | 'covered' | 'gap';
}>`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: ${({theme}) => theme.fontSize.microcopy};
  font-weight: ${({theme}) => theme.fontWeight.bold};
  white-space: nowrap;
  ${({$status, theme}) => {
    if ($status === 'behind')
      return css`
        background: ${theme.colors.red100};
        color: ${theme.colors.red400};
      `;
    if ($status === 'at_risk' || $status === 'overdue')
      return css`
        background: ${theme.colors.orange100};
        color: ${theme.colors.orange400};
      `;
    if ($status === 'on_track' || $status === 'covered')
      return css`
        background: ${theme.colors.green100};
        color: ${theme.colors.green500};
      `;
    if ($status === 'completed')
      return css`
        background: ${theme.colors.purple100};
        color: ${theme.colors.purple300};
      `;
    return css`
      background: ${theme.colors.backgroundDark};
      color: ${theme.colors.textBody};
    `;
  }}
`;

export const Arrow = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-size: 18px;
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const TeamTable = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
  table-layout: fixed;

  col:nth-child(1) {
    width: 20%;
  }

  col:nth-child(2) {
    width: 39%;
  }

  col:nth-child(3) {
    width: 14%;
  }

  col:nth-child(4) {
    width: 12%;
  }

  col:nth-child(5) {
    width: 15%;
  }

  th {
    height: 48px;
    padding: 0 16px;
    border-top: 1px solid ${getColorFromTheme('borderLight')};
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundLight')};
    color: ${getColorFromTheme('textBody')};
    font: 500 13px/18px Graphik, sans-serif;
    text-align: left;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    padding-left: 24px;
  }

  td {
    height: 68px;
    padding: 12px 16px;
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
    color: ${getColorFromTheme('textDark')};
    font: 400 12px/20px Graphik, sans-serif;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  tbody tr:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: -2px;
  }

  @media (max-width: 767px) {
    display: block;
    min-width: 0;

    thead {
      display: none;
    }

    tbody {
      display: grid;
      gap: 10px;
      padding: 12px;
    }

    tbody tr {
      display: block;
      min-width: 0;
      padding: 12px;
      border: 1px solid ${getColorFromTheme('borderLight')};
      border-radius: 10px;
    }

    td,
    td:first-child {
      display: grid;
      width: 100%;
      height: auto;
      grid-template-columns: 82px minmax(0, 1fr);
      gap: 8px;
      padding: 6px 0;
      border: 0;
      overflow-wrap: anywhere;
    }

    td::before {
      content: attr(data-label);
      color: ${getColorFromTheme('textBody')};
      font: 400 12px/20px Graphik, sans-serif;
    }

    td:first-child {
      display: block;
      padding: 0 0 10px;
      margin-bottom: 6px;
      border-bottom: 1px solid ${getColorFromTheme('borderLight')};
    }

    td:first-child::before {
      display: none;
    }
  }
`;

export const PersonCell = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;

  strong,
  small {
    display: block;
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }

  > div:last-child {
    min-width: 0;
  }

  strong,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
  }
`;

export const SignalSummary = styled.div`
  min-width: 0;

  strong,
  small {
    display: block;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
  }

  small {
    overflow: hidden;
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 767px) {
    small {
      overflow: visible;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
`;

export const TeamMapHeader = styled.div`
  display: flex;
  min-height: 54px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
`;

export const TeamMapPanel = styled(Panel)`
  margin-bottom: 16px;
  border-radius: 10px;
`;

export const TeamMapTitle = styled.h2`
  margin: 0;
  color: ${getColorFromTheme('textDark')};
  font: 500 16px/24px Graphik, sans-serif;
`;

export const TeamMapWarning = styled.span`
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
`;

export const TeamMapStatus = styled.span<{
  $status: GoalHealth | 'covered' | 'gap';
}>`
  color: ${({$status, theme}) => {
    if ($status === 'behind') return theme.colors.red400;
    if ($status === 'at_risk') return theme.colors.orange300;
    if ($status === 'on_track' || $status === 'covered')
      return theme.colors.green400;
    if ($status === 'completed') return theme.colors.purple300;
    return theme.colors.textBody;
  }};
  font: 500 12px/20px Graphik, sans-serif;
  white-space: nowrap;
`;

export const TeamMapCheckins = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    font: 400 12px/20px Graphik, sans-serif;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font-weight: 500;
  }

  span {
    color: ${getColorFromTheme('textBody')};
  }
`;

export const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  strong,
  small {
    display: block;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font-size: ${({theme}) => theme.fontSize.body2};
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font-size: ${({theme}) => theme.fontSize.microcopy};
  }
`;

export const StatGrid = styled.div<{$columns?: number}>`
  display: grid;
  grid-template-columns: repeat(
    ${({$columns}) => $columns || 5},
    minmax(0, 1fr)
  );
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const StatCard = styled.div`
  min-width: 0;
  padding: 18px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};

  span,
  small,
  strong {
    display: block;
  }

  span {
    color: ${getColorFromTheme('textBody')};
    font-size: ${({theme}) => theme.fontSize.microcopy};
  }

  strong {
    margin: 5px 0 2px;
    color: ${getColorFromTheme('textDark')};
    font-size: ${({theme}) => theme.fontSize.headline3};
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font-size: ${({theme}) => theme.fontSize.microcopy};
  }
`;

export const GoalSectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  color: ${getColorFromTheme('purple300')};

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 20px/28px Graphik, sans-serif;
  }
`;

export const GoalSection = styled.section`
  padding-top: 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const GoalStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const GoalStatCard = styled.div`
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);

  html[data-theme='dark'] & {
    box-shadow: none;
  }

  > span,
  > strong,
  > small {
    display: block;
  }

  > span {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }

  > strong {
    color: ${getColorFromTheme('blackTwo')};
    font: 500 25px/32px Graphik, sans-serif;
  }
`;

export const GoalStatDelta = styled.small<{
  $direction?: 'positive' | 'negative' | 'neutral';
}>`
  min-height: 20px;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
  white-space: nowrap;

  em {
    color: ${({$direction, theme}) =>
      $direction === 'positive'
        ? theme.colors.green300
        : $direction === 'negative'
        ? theme.colors.red400
        : theme.colors.textBody};
    font-style: normal;
  }
`;

export const FeedbackSection = styled.section`
  padding-top: 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const FeedbackSectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  color: ${getColorFromTheme('purple300')};

  svg {
    width: 18px;
    height: 18px;
  }

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 20px/28px Graphik, sans-serif;
  }
`;

export const FeedbackStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const PersonFeedbackGrid = styled(FeedbackStatGrid)`
  margin-bottom: 0;
`;

export const FeedbackStatCard = styled.div`
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);

  html[data-theme='dark'] & {
    box-shadow: none;
  }

  > span,
  > strong,
  > small {
    display: block;
  }

  > span {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }

  > strong {
    color: ${getColorFromTheme('blackTwo')};
    font: 500 25px/32px Graphik, sans-serif;
  }
`;

export const FeedbackStatNote = styled.small<{
  $direction?: 'positive' | 'negative' | 'neutral';
}>`
  min-height: 20px;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;

  em {
    color: ${({$direction, theme}) =>
      $direction === 'positive'
        ? theme.colors.green300
        : $direction === 'negative'
        ? theme.colors.red400
        : theme.colors.textBody};
    font-style: normal;
  }
`;

export const FeedbackDetailPanel = styled.section`
  overflow: hidden;
  margin-bottom: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const FeedbackDetailHeader = styled.h3`
  margin: 0;
  padding: 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 16px/24px Graphik, sans-serif;
`;

export const FeedbackDetailRow = styled.div`
  display: flex;
  min-height: 60px;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:last-child {
    border-bottom: 0;
  }

  strong {
    overflow: hidden;
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const FeedbackDetailEmpty = styled.div`
  padding: 18px 16px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;

export const GoalDistributionPanel = styled.section`
  margin-bottom: 12px;
  padding: 16px 16px 26px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const GoalPanelTitle = styled.h3`
  margin: 0 0 12px;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 16px/24px Graphik, sans-serif;
`;

export const GoalDistributionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const GoalDistributionLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
`;

export const GoalDistributionLegendItem = styled.div<{$color: string}>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  white-space: nowrap;

  &::before {
    width: 10px;
    height: 10px;
    flex: 0 0 10px;
    border-radius: 50%;
    background: ${({$color}) => $color};
    content: '';
  }

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 500;
  }
`;

export const GoalAttentionPanel = styled.section`
  overflow: hidden;
  margin-bottom: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const GoalAttentionHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  h3 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 500 16px/24px Graphik, sans-serif;
  }
`;

export const GoalAttentionRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 68px;
  align-items: center;
  gap: 24px;
  padding: 12px 16px 12px 22px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: none;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: -2px;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 14px 16px;
  }
`;

export const GoalAttentionIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;

  > div:last-child {
    min-width: 0;
  }

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }
`;

export const GoalAttentionProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    margin-left: 44px;
  }
`;

export const GoalAttentionTrack = styled.span`
  display: block;
  width: 68px;
  height: 4px;
  overflow: hidden;
  border-radius: 8px;
  background: ${getColorFromTheme('borderLight')};
`;

export const GoalAttentionBar = styled.span<{
  $progress: number;
  $status: GoalHealth;
}>`
  display: block;
  width: ${({$progress}) => `${Math.max(0, Math.min(100, $progress))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({$status, theme}) =>
    $status === 'behind' ? theme.colors.red400 : theme.colors.orange300};
`;

export const GoalAttentionPercent = styled.span`
  min-width: 34px;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 12px/20px Graphik, sans-serif;
  text-align: right;
`;

export const GoalAttentionStatus = styled.span<{$status: GoalHealth}>`
  min-width: 48px;
  color: ${({$status, theme}) =>
    $status === 'behind' ? theme.colors.red400 : theme.colors.orange300};
  font: 500 12px/20px Graphik, sans-serif;
  white-space: nowrap;
`;

export const Distribution = styled.div`
  display: flex;
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: ${getColorFromTheme('borderLight')};
`;

export const DistributionSegment = styled.span<{
  $color: string;
  $width: number;
}>`
  width: ${({$width}) => `${$width}%`};
  min-width: ${({$width}) => ($width ? '2px' : '0')};
  background: ${({$color}) => $color};
`;

export const DetailBody = styled.div`
  padding: 22px 24px;
`;

export const EmptyState = styled.div`
  padding: 48px 24px;
  color: ${getColorFromTheme('textBody')};
  font-size: ${({theme}) => theme.fontSize.body2};
  line-height: ${({theme}) => theme.lineHeight.body2};
  text-align: center;

  strong {
    display: block;
    margin-bottom: 6px;
    color: ${getColorFromTheme('textDark')};
    font-size: ${({theme}) => theme.fontSize.headline4};
  }
`;

export const RetryButton = styled.button`
  margin-top: 14px;
  padding: 8px 14px;
  border: 1px solid ${getColorFromTheme('purple300')};
  border-radius: 8px;
  color: ${getColorFromTheme('purple300')};
  font-size: ${({theme}) => theme.fontSize.cta};
  font-weight: ${({theme}) => theme.fontWeight.bold};

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const LoaderBox = styled.div`
  min-height: 220px;
  padding: 80px 0;
`;

export const CheckinSection = styled.section`
  padding-top: 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const CheckinSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

export const CheckinSectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 20px/28px Graphik, sans-serif;
  }
`;

export const PersonParticipationPanel = styled.section`
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const PersonParticipationHeader = styled.h3`
  margin: 0;
  padding: 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('textDark')};
  font: 500 16px/24px Graphik, sans-serif;
`;

export const PersonParticipationRow = styled.div`
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 16px 12px 22px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:last-child {
    border-bottom: 0;
  }
`;

export const PersonParticipationIdentity = styled.div`
  min-width: 0;

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }
`;

export const PersonParticipationStatus = styled.span<{
  $status: 'submitted' | 'overdue' | 'outstanding';
}>`
  flex: 0 0 auto;
  color: ${({$status, theme}) =>
    $status === 'submitted'
      ? theme.colors.green300
      : $status === 'overdue'
      ? theme.colors.orange300
      : theme.colors.textBody};
  font: 500 12px/20px Graphik, sans-serif;
`;

export const ProgramSelector = styled.div`
  position: relative;
  width: fit-content;
  margin-bottom: 12px;
`;

export const ProgramSelectorButton = styled.button<{$open: boolean}>`
  display: flex;
  min-width: 224px;
  height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
  border: 1px solid
    ${({$open, theme}) =>
      $open ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textDark')};
  font: 500 14px/22px Graphik, sans-serif;
  text-align: left;

  > span {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > svg {
    flex: 0 0 auto;
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
    transition: transform 160ms ease;
  }

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const ProgramStatusDot = styled.span<{$hasOutstanding: boolean}>`
  width: 10px;
  height: 10px;
  flex: 0 0 10px;
  border-radius: 50%;
  background: ${({$hasOutstanding, theme}) =>
    $hasOutstanding ? theme.colors.orange300 : theme.colors.green300};
`;

export const ProgramSelectorMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  width: max(224px, 100%);
  max-width: min(360px, calc(100vw - 40px));
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const ProgramSelectorOption = styled.button<{$active: boolean}>`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('textDark') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  > span {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > svg {
    flex: 0 0 auto;
    color: ${getColorFromTheme('purple300')};
  }

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textDark')};
  }
`;

export const ProgramSummaryPanel = styled.section`
  margin-bottom: 12px;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);

  html[data-theme='dark'] & {
    box-shadow: none;
  }
`;

export const ProgramSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1.35fr repeat(3, minmax(0, 1fr));
  gap: 60px;
  align-items: center;

  @media (max-width: 900px) {
    gap: 24px;
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const ProgramSummaryItem = styled.div`
  min-width: 0;

  span,
  strong,
  small {
    display: block;
  }

  span {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }

  strong {
    overflow: hidden;
    color: ${getColorFromTheme('blackTwo')};
    font: 500 20px/28px Graphik, sans-serif;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }
`;

export const ProgramCadence = styled.span`
  display: block;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const ProgramInsightGrid = styled.div`
  display: grid;
  grid-template-columns: 268px minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const ProgramAverageCard = styled.div`
  display: flex;
  min-width: 0;
  height: 110px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);

  html[data-theme='dark'] & {
    box-shadow: none;
  }

  > span {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }

  > strong {
    color: ${getColorFromTheme('blackTwo')};
    font: 500 24px/28px Graphik, sans-serif;
  }
`;

export const ProgramAverageDelta = styled.small<{
  $direction: 'positive' | 'negative' | 'neutral';
}>`
  color: ${({$direction, theme}) =>
    $direction === 'positive'
      ? theme.colors.green300
      : $direction === 'negative'
      ? theme.colors.red400
      : theme.colors.textBody};
  font: 400 12px/20px Graphik, sans-serif;

  span {
    color: ${getColorFromTheme('textBody')};
  }
`;

export const ProgramTrendPanel = styled.section`
  display: flex;
  min-width: 0;
  height: 110px;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);

  html[data-theme='dark'] & {
    box-shadow: none;
  }
`;

export const ProgramTrendTitle = styled.h3`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
`;

export const Trend = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(32px, 1fr));
  gap: 16px;
  align-items: end;
  height: 66px;
`;

export const TrendItem = styled.div`
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
`;

export const TrendBarContainer = styled.div`
  display: flex;
  width: 100%;
  height: 38px;
  align-items: flex-end;

  > div,
  > div > div:first-child {
    width: 100%;
  }
`;

export const TrendBar = styled.div<{$height: number; $active: boolean}>`
  width: 100%;
  height: ${({$height}) => `${Math.max(8, Math.round($height * 0.38))}px`};
  background: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.purple200};

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const ProgramOutstandingPanel = styled.section`
  overflow: hidden;
  margin-bottom: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const ProgramOutstandingHeader = styled.h3`
  display: flex;
  min-height: 54px;
  align-items: center;
  margin: 0;
  padding: 14px 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 16px/24px Graphik, sans-serif;
`;

export const ProgramOutstandingRow = styled(Link)`
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 16px 12px 22px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: inherit;
  text-decoration: none;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: -2px;
  }
`;

export const ProgramOutstandingIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;

  > div:last-child {
    min-width: 0;
  }

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${getColorFromTheme('textDark')};
    font: 500 14px/22px Graphik, sans-serif;
  }

  small {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }
`;

export const ProgramOutstandingStatus = styled.span`
  flex: 0 0 auto;
  color: ${getColorFromTheme('orange300')};
  font: 500 12px/20px Graphik, sans-serif;
`;

export const ProgramOutstandingEmpty = styled.div`
  padding: 20px 22px;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  white-space: nowrap;
`;
